// TODO Argument of type 'CalendarDate' is not assignable to parameter of type 'AnyCalendarDate'.
// @ts-nocheck
import { Button } from '@florencelabs/button';
import { Collapse } from '@florencelabs/collapse';
import { MaterialSymbol } from '@florencelabs/icons';
import { ModalCloseButton, ModalWithTrigger } from '@florencelabs/modal-2';
import { Tooltip } from '@florencelabs/tooltip';
import { CalendarDate } from '@internationalized/date';
import clsx from 'clsx';
import {
  useEffect,
  useState,
  useMemo,
  useContext,
  useId,
  ReactElement,
} from 'react';
import { I18nProvider } from 'react-aria';
import {
  Button as RACButton,
  CalendarStateContext,
} from 'react-aria-components';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useToggle } from 'usehooks-ts';
import { supportedLocales } from '@provider-directory/app/i18n/supported-locales';
import { useTimeZone } from '@provider-directory/components/date-picker/use-time-zone';
import { useCachedSlots } from '@provider-directory/contexts/CachedSlotsContext';
import { useFeatureFlags } from '@provider-directory/contexts/FlagContext';
import {
  formatDateToFullDateString,
  parseCalendarDate,
} from '@provider-directory/functions/date';
import { useAvailabilityWindow } from '@provider-directory/hooks/useAvailabilityWindow';
import { useIsDesktop } from '@provider-directory/hooks/useBreakpoints';
import { useFetchableDateRange } from '@provider-directory/hooks/useCalendar';
import {
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  CalendarHeading,
  CalendarHeadingButton,
} from '../../date-picker/calendar.component';
import { AppointmentTimeFormFieldValues } from '../select-appointment-form-values';
import { CalendarSkeleton } from './select-appointment-skeletons.component';

const CalendarNextButtonTooltip = ({
  lastFetchableDate,
  onPress,
}: {
  lastFetchableDate: CalendarDate;
  onPress?: () => void;
}): ReactElement => {
  const [t] = useTranslation();
  const maxDays = useAvailabilityWindow();
  const calendarContext = useContext(CalendarStateContext);
  const isLastMonth = useMemo(() => {
    if (!calendarContext) {
      return false;
    }
    return calendarContext.visibleRange.end.compare(lastFetchableDate) >= 0;
  }, [calendarContext, lastFetchableDate]);
  const [isDrawerOpen, toggleDrawer] = useToggle(false);
  const isDesktop = useIsDesktop();
  return (
    <>
      {isLastMonth ? (
        !isDesktop ? (
          <ModalWithTrigger
            isOpen={isDrawerOpen}
            onOpenChange={toggleDrawer}
            trigger={
              <RACButton
                data-testid={'controlled-calendar-next-tooltip-btn'}
                className={'h-6 w-6 cursor-default text-gray-950/30'}
                onPress={toggleDrawer}
                slot={null}
                aria-label={t('aria.moreInfo')}
              >
                <MaterialSymbol name={'chevron_right'} />
              </RACButton>
            }
            variant={'bottomDrawer'}
            modalClassName={'rounded-t-lg h-fit w-full'}
            dialogClassName={'px-0 pt-8 pb-12 gap-y-6'}
            overlayClassName={'bg-black/20'}
          >
            <div
              className={
                'flex min-h-[54px] flex-col items-center justify-between border-b border-black border-opacity-10'
              }
            >
              <div className={'flex w-full items-center px-6 tablet:px-16'}>
                <div className={'flex items-center gap-x-4'}>
                  <MaterialSymbol name={'info'} size={24} />
                  {t('searchResults.appointmentTitle')}
                </div>
                <ModalCloseButton
                  slot={null}
                  className={'relative ml-auto inline-block h-6 w-6 !py-0'}
                  onPress={toggleDrawer}
                  ariaLabel={t('aria.closeCalendar')}
                />
              </div>
            </div>
            <p
              className={
                'px-6 text-body-small text-gray-800 tablet:px-16 tablet:text-body-medium'
              }
            >
              {t('searchResults.appointmentText')}
            </p>
          </ModalWithTrigger>
        ) : (
          <Tooltip
            data-testid={'controlled-calendar-next-tooltip'}
            tooltipText={t('appointmentSlot.appointmentDate.maxDateTooltip', {
              maxDays,
            })}
          >
            <RACButton
              data-testid={'controlled-calendar-next-tooltip-btn'}
              className={'h-6 w-6 cursor-default text-gray-950/30'}
              slot={null}
              aria-label={t('aria.moreInfo')}
            >
              <MaterialSymbol name={'chevron_right'} />
            </RACButton>
          </Tooltip>
        )
      ) : null}
      <CalendarHeadingButton
        className={isLastMonth ? 'sr-only' : ''}
        slot={'next'}
        onPress={onPress}
      />
    </>
  );
};

export const ControlledAppointmentCalendar = ({
  shownByDefault,
  onChange,
  reasonForVisit,
}: {
  shownByDefault: boolean;
  onChange?: () => void;
  reasonForVisit: string;
}): ReactElement => {
  const slotDateLabelId = useId();
  const [t, i18n] = useTranslation();
  const [isCalendarShown, setIsCalendarShown] =
    useState<boolean>(shownByDefault);
  const timeZone = useTimeZone();
  const { control, watch, setValue } =
    useFormContext<AppointmentTimeFormFieldValues>();
  const departmentId = watch('departmentId');
  const slotDateString = watch('slotDate');
  const slotDate = useMemo(
    () => parseCalendarDate(slotDateString ?? ''),
    [slotDateString],
  );

  const { field } = useController<AppointmentTimeFormFieldValues, 'slotDate'>({
    name: 'slotDate',
    control,
  });
  const [focusedDate, setFocusedDate] = useState<CalendarDate | undefined>(
    field.value ? parseCalendarDate(field.value) : undefined,
  );

  const { cachedSlots, isLoading } = useCachedSlots();
  const [todaysDate, lastFetchableDate] = useFetchableDateRange();
  const {
    featureFlags: { allowAnyDateInCalendar },
  } = useFeatureFlags();

  const datesAvailable = useMemo<Set<string>>(() => {
    if (!cachedSlots || !reasonForVisit || !departmentId) {
      return new Set();
    }
    return new Set(Object.keys(cachedSlots[departmentId] ?? {}));
  }, [cachedSlots, reasonForVisit, departmentId]);

  const minDateAvailable = useMemo(() => {
    return parseCalendarDate(Array.from(datesAvailable).sort()[0]);
  }, [datesAvailable]);

  const maxDateAvailable = useMemo(() => {
    const datesFetched = Array.from(datesAvailable).sort();
    const lastDateFetched = parseCalendarDate(
      datesFetched[datesFetched.length - 1],
    );

    // Clamp max date to the configured availability window
    if ((lastDateFetched?.compare(lastFetchableDate) ?? -1) > 0) {
      return lastFetchableDate;
    }
    return lastDateFetched;
  }, [datesAvailable, lastFetchableDate]);

  // Set the initial slot date to the first available date
  useEffect(() => {
    if (!minDateAvailable) return;

    const parsedValue = field.value ? parseCalendarDate(field.value) : null;

    const shouldUpdate = !parsedValue;

    if (shouldUpdate) {
      setValue('slotDate', minDateAvailable.toString());
    }
  }, [field.value, minDateAvailable, setValue]);

  useEffect(() => {
    if (!focusedDate && field.value) {
      setFocusedDate(parseCalendarDate(field.value));
    }
  }, [field.value, focusedDate]);

  useEffect(() => {
    if (slotDateString) {
      setFocusedDate(parseCalendarDate(slotDateString));
    }
  }, [slotDateString]);

  return isLoading ? (
    <CalendarSkeleton />
  ) : (
    <div data-testid={'controlled-calendar'}>
      <div className={'flex flex-col gap-2'}>
        <div
          className={
            'flex flex-col tablet:flex-row tablet:items-start tablet:justify-between'
          }
        >
          <div className={'flex flex-col'}>
            {slotDate && (
              <span
                data-testid={'appointment-date'}
                className={'text-body-medium-semibold'}
              >
                {formatDateToFullDateString({
                  dateString: slotDate.toString(),
                  locale: supportedLocales.get(i18n.language)?.locale,
                })}
              </span>
            )}
            <span className={'text-display-2xsmall font-medium text-gray-700'}>
              {t('schedule.timeZone')}
            </span>
          </div>
          <Button
            testId={'calendar-toggle-btn'}
            aria-label={
              isCalendarShown
                ? t('schedule.closeDatePicker')
                : t('schedule.viewMoreDates')
            }
            onPress={() => {
              setIsCalendarShown(!isCalendarShown);
            }}
            variant={'clientLink'}
            className={
              'flex cursor-pointer select-none items-center gap-x-2 text-right'
            }
          >
            {t('schedule.viewMoreDates')}
            <MaterialSymbol
              className={clsx('transition-transform duration-300', {
                'rotate-180': !isCalendarShown,
              })}
              name={'keyboard_arrow_up'}
            />
          </Button>
        </div>
      </div>

      <Collapse open={isCalendarShown}>
        <span className={'sr-only'} id={slotDateLabelId}>
          {t('appointmentSlot.appointmentType.label')}
        </span>
        <I18nProvider locale={i18n.language}>
          <Calendar
            {...field}
            aria-labelledby={slotDateLabelId}
            value={field.value ? parseCalendarDate(field.value) : null}
            onChange={(date) => {
              field.onChange(date.toString());
              if (onChange) {
                onChange();
              }
            }}
            isDateUnavailable={(date) =>
              allowAnyDateInCalendar
                ? date.toString() < todaysDate.toString() ||
                  date.toString() > lastFetchableDate.toString()
                : !datesAvailable.has(date.toString())
            }
            minValue={todaysDate}
            maxValue={lastFetchableDate}
            focusedValue={focusedDate ?? todaysDate}
            onFocusChange={setFocusedDate}
            isDisabled={!maxDateAvailable}
            className={'w-[calc(100%+2rem)] -translate-x-4 pt-4'}
          >
            <div className={'mb-6 flex items-center justify-between px-4'}>
              <CalendarHeading />
              <div className={'flex items-center justify-center gap-x-6'}>
                <CalendarHeadingButton slot={'previous'} />
                <CalendarNextButtonTooltip
                  lastFetchableDate={lastFetchableDate}
                />
              </div>
            </div>
            <CalendarGrid>
              <CalendarGridHeader>
                {(day) => (
                  <CalendarHeaderCell
                    className={'text-center text-body-small-medium'}
                  >
                    {day}
                  </CalendarHeaderCell>
                )}
              </CalendarGridHeader>
              <CalendarGridBody>
                {(date) => <CalendarCell date={date} timeZone={timeZone} />}
              </CalendarGridBody>
            </CalendarGrid>
          </Calendar>
        </I18nProvider>
      </Collapse>
    </div>
  );
};
